// Adapted from OpenCharts (MIT, see LICENSE-OpenCharts): theme colours
// instead of fixed dark-mode ones, one-hue fib levels, plain-English readouts.
import type { IPrimitivePaneRenderer } from "lightweight-charts";
import { withAlpha } from "./geometry";
import { TEXT_SIZE, textBox } from "./hitTest";
import { MAX_COST_R, costRFor } from "@/components/daytrader/dayTrader";
import type { Drawing, DrawingLineStyle, DrawInfo, DrawTheme, ResolvedEntry, ResolvedFibLevel } from "./types";

type Target = Parameters<IPrimitivePaneRenderer["draw"]>[0];
export type Scope = Parameters<Parameters<Target["useBitmapCoordinateSpace"]>[0]>[0];

const HANDLE_RADIUS = 5;
const LINE_WIDTH = 2;
const SHAPE_FILL_ALPHA = 0.14;

interface BPt {
  x: number;
  y: number;
}

function toBitmap(scope: Scope, x: number, y: number): BPt {
  return { x: Math.round(x * scope.horizontalPixelRatio), y: Math.round(y * scope.verticalPixelRatio) };
}

function showHandles(e: ResolvedEntry): boolean {
  return e.state === "hovered" || e.state === "selected" || e.state === "preview";
}

function dashFor(style: DrawingLineStyle | undefined): readonly number[] | undefined {
  if (style === "dashed") return [6, 6];
  if (style === "dotted") return [2, 3];
  return undefined;
}

function applyDash(scope: Scope, dash: readonly number[] | undefined): void {
  if (dash) scope.context.setLineDash(dash.map(v => v * scope.horizontalPixelRatio));
}

function strokeLine(scope: Scope, a: BPt, b: BPt, color: string, widthMedia: number, dash?: readonly number[]): void {
  const ctx = scope.context;
  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = widthMedia * scope.verticalPixelRatio;
  applyDash(scope, dash);
  ctx.beginPath();
  ctx.moveTo(a.x, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.stroke();
  ctx.restore();
}

function drawHandle(scope: Scope, p: BPt, color: string, theme: DrawTheme): void {
  const ctx = scope.context;
  ctx.save();
  ctx.beginPath();
  ctx.arc(p.x, p.y, HANDLE_RADIUS * scope.horizontalPixelRatio, 0, Math.PI * 2);
  ctx.fillStyle = theme.surface;
  ctx.fill();
  ctx.lineWidth = 1.5 * scope.horizontalPixelRatio;
  ctx.strokeStyle = color;
  ctx.stroke();
  ctx.restore();
}

function fillAndStroke(scope: Scope, d: Drawing): void {
  const ctx = scope.context;
  ctx.fillStyle = withAlpha(d.color, SHAPE_FILL_ALPHA);
  ctx.fill();
  ctx.strokeStyle = d.color;
  ctx.lineWidth = (d.width ?? 1.5) * scope.verticalPixelRatio;
  applyDash(scope, dashFor(d.lineStyle));
  ctx.stroke();
}

export function renderEntry(scope: Scope, e: ResolvedEntry, info: DrawInfo): void {
  switch (e.d.type) {
    case "trendline":
      return renderTrendline(scope, e, info);
    case "horizontal":
      return renderHorizontal(scope, e, info.theme);
    case "vertical":
      return renderVertical(scope, e, info.theme);
    case "rectangle":
      return renderRectangle(scope, e, info.theme);
    case "ellipse":
      return renderEllipse(scope, e, info.theme);
    case "triangle":
      return renderTriangle(scope, e, info.theme);
    case "arrow":
      return renderArrow(scope, e, info.theme);
    case "channel":
      return renderChannel(scope, e, info.theme);
    case "fibonacci":
    case "fibextension":
      return renderFibonacci(scope, e, info.theme);
    case "position":
      return renderPosition(scope, e, info);
    case "text":
      return renderText(scope, e, info.theme);
  }
}

// ── Lines ──────────────────────────────────────────────────────────────────

function renderTrendline(scope: Scope, e: ResolvedEntry, info: DrawInfo): void {
  const { theme } = info;
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null) return;
  const a = toBitmap(scope, e.x1, e.y1);
  const b = toBitmap(scope, e.x2, e.y2);
  const seg = e.seg ?? { x1: e.x1, y1: e.y1, x2: e.x2, y2: e.y2 };
  strokeLine(
    scope,
    toBitmap(scope, seg.x1, seg.y1),
    toBitmap(scope, seg.x2, seg.y2),
    e.d.color,
    e.d.width ?? LINE_WIDTH,
    dashFor(e.d.lineStyle),
  );
  if (showHandles(e)) {
    drawHandle(scope, a, e.d.color, theme);
    drawHandle(scope, b, e.d.color, theme);
  }
  if (e.stats && e.stats.length > 0) {
    const angle = Math.round((Math.atan2(a.y - b.y, b.x - a.x) * 180) / Math.PI);
    const gap = 10 * scope.horizontalPixelRatio;
    drawLabelBox(scope, b.x + gap, b.y + 10 * scope.verticalPixelRatio, [...e.stats, `${angle}°`], e.d.color, info, b.x - gap);
  }
}

function renderHorizontal(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.y1 === null) return;
  const y = Math.round(e.y1 * scope.verticalPixelRatio);
  strokeLine(scope, { x: 0, y }, { x: scope.bitmapSize.width, y }, e.d.color, e.d.width ?? 1.5, dashFor(e.d.lineStyle));
  if (showHandles(e)) drawHandle(scope, { x: Math.round(scope.bitmapSize.width / 2), y }, e.d.color, theme);
}

function renderVertical(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.x1 === null) return;
  const x = Math.round(e.x1 * scope.horizontalPixelRatio);
  strokeLine(scope, { x, y: 0 }, { x, y: scope.bitmapSize.height }, e.d.color, e.d.width ?? 1.5, dashFor(e.d.lineStyle));
  if (showHandles(e)) drawHandle(scope, { x, y: Math.round(scope.bitmapSize.height / 2) }, e.d.color, theme);
}

function renderArrow(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null) return;
  const a = toBitmap(scope, e.x1, e.y1);
  const b = toBitmap(scope, e.x2, e.y2);
  strokeLine(scope, a, b, e.d.color, e.d.width ?? LINE_WIDTH, dashFor(e.d.lineStyle));
  const ctx = scope.context;
  const ang = Math.atan2(b.y - a.y, b.x - a.x);
  const len = 12 * scope.horizontalPixelRatio;
  const spread = Math.PI / 7;
  ctx.save();
  ctx.fillStyle = e.d.color;
  ctx.beginPath();
  ctx.moveTo(b.x, b.y);
  ctx.lineTo(b.x - len * Math.cos(ang - spread), b.y - len * Math.sin(ang - spread));
  ctx.lineTo(b.x - len * Math.cos(ang + spread), b.y - len * Math.sin(ang + spread));
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  if (showHandles(e)) {
    drawHandle(scope, a, e.d.color, theme);
    drawHandle(scope, b, e.d.color, theme);
  }
}

// Parallel channel: the anchor line, a copy offset by (price3 − price), and a
// light fill between them.
function renderChannel(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null || e.y3 == null) return;
  const dy = e.y3 - e.y1;
  const a1 = toBitmap(scope, e.x1, e.y1);
  const b1 = toBitmap(scope, e.x2, e.y2);
  const a2 = toBitmap(scope, e.x1, e.y3);
  const b2 = toBitmap(scope, e.x2, e.y2 + dy);
  const ctx = scope.context;
  ctx.save();
  ctx.fillStyle = withAlpha(e.d.color, 0.1);
  ctx.beginPath();
  ctx.moveTo(a1.x, a1.y);
  ctx.lineTo(b1.x, b1.y);
  ctx.lineTo(b2.x, b2.y);
  ctx.lineTo(a2.x, a2.y);
  ctx.closePath();
  ctx.fill();
  ctx.restore();
  const dash = dashFor(e.d.lineStyle);
  strokeLine(scope, a1, b1, e.d.color, e.d.width ?? LINE_WIDTH, dash);
  strokeLine(scope, a2, b2, e.d.color, e.d.width ?? LINE_WIDTH, dash);
  if (showHandles(e)) {
    drawHandle(scope, a1, e.d.color, theme);
    drawHandle(scope, b1, e.d.color, theme);
    drawHandle(scope, a2, e.d.color, theme);
  }
}

// ── Shapes ─────────────────────────────────────────────────────────────────

function drawCornerHandles(scope: Scope, a: BPt, b: BPt, color: string, theme: DrawTheme): void {
  drawHandle(scope, a, color, theme);
  drawHandle(scope, b, color, theme);
  drawHandle(scope, { x: a.x, y: b.y }, color, theme);
  drawHandle(scope, { x: b.x, y: a.y }, color, theme);
}

function renderRectangle(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null) return;
  const a = toBitmap(scope, e.x1, e.y1);
  const b = toBitmap(scope, e.x2, e.y2);
  const ctx = scope.context;
  ctx.save();
  ctx.beginPath();
  ctx.rect(Math.min(a.x, b.x), Math.min(a.y, b.y), Math.abs(b.x - a.x), Math.abs(b.y - a.y));
  fillAndStroke(scope, e.d);
  ctx.restore();
  if (showHandles(e)) drawCornerHandles(scope, a, b, e.d.color, theme);
}

function renderEllipse(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null) return;
  const a = toBitmap(scope, e.x1, e.y1);
  const b = toBitmap(scope, e.x2, e.y2);
  const ctx = scope.context;
  ctx.save();
  ctx.beginPath();
  ctx.ellipse((a.x + b.x) / 2, (a.y + b.y) / 2, Math.abs(b.x - a.x) / 2, Math.abs(b.y - a.y) / 2, 0, 0, Math.PI * 2);
  fillAndStroke(scope, e.d);
  ctx.restore();
  if (showHandles(e)) drawCornerHandles(scope, a, b, e.d.color, theme);
}

function renderTriangle(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.x1 === null || e.y1 === null || e.x2 === null || e.y2 === null) return;
  const a = toBitmap(scope, e.x1, e.y1);
  const b = toBitmap(scope, e.x2, e.y2);
  const ctx = scope.context;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo((a.x + b.x) / 2, a.y);
  ctx.lineTo(b.x, b.y);
  ctx.lineTo(a.x, b.y);
  ctx.closePath();
  fillAndStroke(scope, e.d);
  ctx.restore();
  if (showHandles(e)) {
    drawHandle(scope, a, e.d.color, theme);
    drawHandle(scope, b, e.d.color, theme);
  }
}

// ── Text ───────────────────────────────────────────────────────────────────

function renderText(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.x1 === null || e.y1 === null) return;
  const ctx = scope.context;
  const hpr = scope.horizontalPixelRatio;
  const vpr = scope.verticalPixelRatio;
  const text = e.d.text || "Text";
  const lines = text.split("\n");
  const box = textBox(text, TEXT_SIZE);
  const at = toBitmap(scope, e.x1, e.y1);
  const pad = 4 * hpr;
  ctx.save();
  // A light backing keeps the note readable over candles.
  ctx.fillStyle = withAlpha(theme.surface, 0.8);
  ctx.beginPath();
  ctx.roundRect(at.x - pad, at.y - pad, box.w * hpr + pad * 2, box.h * vpr + pad * 2, 4 * hpr);
  ctx.fill();
  if (showHandles(e)) {
    ctx.strokeStyle = e.d.color;
    ctx.lineWidth = hpr;
    ctx.setLineDash([3 * hpr, 3 * hpr]);
    ctx.stroke();
  }
  ctx.font = `${Math.round(TEXT_SIZE * vpr)}px ${theme.font}`;
  ctx.textBaseline = "top";
  ctx.fillStyle = e.d.color;
  lines.forEach((l, i) => ctx.fillText(l, at.x, at.y + i * TEXT_SIZE * 1.3 * vpr));
  ctx.restore();
}

// ── Fibonacci ──────────────────────────────────────────────────────────────

function renderFibonacci(scope: Scope, e: ResolvedEntry, theme: DrawTheme): void {
  if (e.x1 === null || e.x2 === null || !e.fibLevels || e.fibLevels.length === 0) return;
  const xA = Math.round(Math.min(e.x1, e.x2) * scope.horizontalPixelRatio);
  const xB = Math.round(Math.max(e.x1, e.x2) * scope.horizontalPixelRatio);
  renderFibBands(scope, e.fibLevels, xA, xB, e.d.color);
  for (const lvl of e.fibLevels) renderFibLevel(scope, lvl, xA, xB, e.d.color, theme);
  if (e.y1 === null || e.y2 === null) return;
  // The dashed diagonal between the anchors, where the drag handles live.
  const a = toBitmap(scope, e.x1, e.y1);
  const b = toBitmap(scope, e.x2, e.y2);
  strokeLine(scope, a, b, withAlpha(e.d.color, 0.6), 1, [4, 4]);
  if (showHandles(e)) {
    drawHandle(scope, a, e.d.color, theme);
    drawHandle(scope, b, e.d.color, theme);
  }
}

function renderFibBands(scope: Scope, levels: ResolvedFibLevel[], xA: number, xB: number, color: string): void {
  const ctx = scope.context;
  for (let i = 0; i < levels.length - 1; i++) {
    const top = levels[i]!;
    const bottom = levels[i + 1]!;
    if (top.y === null || bottom.y === null) continue;
    const yA = Math.round(top.y * scope.verticalPixelRatio);
    const yB = Math.round(bottom.y * scope.verticalPixelRatio);
    ctx.fillStyle = withAlpha(color, i % 2 === 0 ? 0.05 : 0.1);
    ctx.fillRect(xA, Math.min(yA, yB), xB - xA, Math.abs(yB - yA));
  }
}

function renderFibLevel(scope: Scope, lvl: ResolvedFibLevel, xA: number, xB: number, color: string, theme: DrawTheme): void {
  if (lvl.y === null) return;
  const y = Math.round(lvl.y * scope.verticalPixelRatio);
  strokeLine(scope, { x: xA, y }, { x: xB, y }, color, 1);
  const ctx = scope.context;
  const gap = 6 * scope.horizontalPixelRatio;
  ctx.save();
  ctx.font = `${Math.round(10 * scope.verticalPixelRatio)}px ${theme.font}`;
  ctx.textBaseline = "middle";
  ctx.fillStyle = color;
  // Label left of the levels, or just inside them when there is no room.
  const fits = ctx.measureText(lvl.label).width + gap <= xA;
  ctx.textAlign = fits ? "right" : "left";
  ctx.fillText(lvl.label, fits ? xA - gap : xA + gap, y - (fits ? 0 : 7 * scope.verticalPixelRatio));
  ctx.restore();
}

// ── Long / short position (stop + target box) ──────────────────────────────

function renderPosition(scope: Scope, e: ResolvedEntry, info: DrawInfo): void {
  const { x1, x2, y1, yStop, yTarget } = e;
  if (x1 === null || x2 === null || y1 === null || yStop == null || yTarget == null) return;
  const { theme } = info;
  const xa = Math.min(x1, x2);
  const xb = Math.max(x1, x2);
  posZone(scope, xa, xb, y1, yTarget, theme.profit);
  posZone(scope, xa, xb, y1, yStop, theme.loss);
  strokeLine(scope, toBitmap(scope, xa, y1), toBitmap(scope, xb, y1), theme.text, 1.5, [5, 3]);
  if (showHandles(e)) {
    const xm = (xa + xb) / 2;
    drawHandle(scope, toBitmap(scope, xm, y1), theme.text, theme);
    drawHandle(scope, toBitmap(scope, xm, yTarget), theme.profit, theme);
    drawHandle(scope, toBitmap(scope, xm, yStop), theme.loss, theme);
    drawHandle(scope, toBitmap(scope, xb, y1), theme.text, theme);
  }
  const lines = positionReadout(e.d, info);
  if (lines.length > 0) {
    const at = toBitmap(scope, xb + 8, y1);
    const border = e.d.side === "short" ? theme.loss : theme.profit;
    drawLabelBox(scope, at.x, at.y, lines, border, info, toBitmap(scope, xa - 8, y1).x);
  }
}

function posZone(scope: Scope, xa: number, xb: number, yFrom: number, yTo: number, color: string): void {
  const ctx = scope.context;
  const a = toBitmap(scope, xa, Math.min(yFrom, yTo));
  const b = toBitmap(scope, xb, Math.max(yFrom, yTo));
  ctx.save();
  ctx.fillStyle = withAlpha(color, 0.14);
  ctx.fillRect(a.x, a.y, b.x - a.x, b.y - a.y);
  ctx.strokeStyle = withAlpha(color, 0.6);
  ctx.lineWidth = scope.verticalPixelRatio;
  ctx.strokeRect(a.x, a.y, b.x - a.x, b.y - a.y);
  ctx.restore();
}

/** The box's numbers in plain words: reward vs risk, target, stop, size and fees. */
export function positionReadout(d: Drawing, info: Pick<DrawInfo, "riskUsd" | "priceFormat">): string[] {
  if (d.stopPrice == null || d.targetPrice == null) return [];
  const reward = Math.abs(d.targetPrice - d.price);
  const risk = Math.abs(d.price - d.stopPrice);
  const rr = risk > 0 ? reward / risk : 0;
  const lines = [
    `${d.side === "short" ? "Sell" : "Buy"} · reward ${rr.toFixed(2)}× risk`,
    `Target ${info.priceFormat(d.targetPrice)}  ${signPct(d.targetPrice, d.price)}`,
    `Entry  ${info.priceFormat(d.price)}`,
    `Stop   ${info.priceFormat(d.stopPrice)}  ${signPct(d.stopPrice, d.price)}`,
  ];
  if (info.riskUsd > 0 && risk > 0) {
    const qty = info.riskUsd / risk;
    const value = qty * d.price;
    lines.push(`Risk $${info.riskUsd.toFixed(2)} → ${qty >= 100 ? qty.toFixed(0) : qty.toPrecision(3)} coins ($${value >= 100 ? value.toFixed(0) : value.toFixed(2)})`);
  }
  // Fees + slippage in R: a tight stop makes them a big share of the risk.
  const cost = costRFor(d.price, d.stopPrice);
  if (Number.isFinite(cost)) {
    lines.push(cost > MAX_COST_R ? `Fees ${cost.toFixed(2)}R: stop too tight` : `Fees ≈ ${cost.toFixed(2)}R`);
  }
  return lines;
}

function signPct(v: number, entry: number): string {
  if (entry === 0) return "0%";
  const p = ((v - entry) / entry) * 100;
  return `${p >= 0 ? "+" : "−"}${Math.abs(p).toFixed(2)}%`;
}

// ── Readout box ────────────────────────────────────────────────────────────

/**
 * A readout box with its left edge at x — or its right edge at altX when it
 * would run into the right-edge lane, where the price-line titles paint over
 * every primitive.
 */
function drawLabelBox(
  scope: Scope, x: number, y: number, lines: string[], border: string, info: DrawInfo, altX: number,
): void {
  const { theme } = info;
  const ctx = scope.context;
  const hpr = scope.horizontalPixelRatio;
  const vpr = scope.verticalPixelRatio;
  ctx.save();
  ctx.font = `${Math.round(10 * vpr)}px ${theme.font}`;
  const lineH = 13 * vpr;
  const pad = 6 * hpr;
  const width = Math.max(...lines.map(l => ctx.measureText(l).width)) + pad * 2;
  const height = lines.length * lineH + pad;
  const laneEdge = scope.bitmapSize.width - info.rightLane * hpr;
  const bx = Math.max(0, Math.min(x + width > laneEdge ? altX - width : x, scope.bitmapSize.width - width));
  const by = Math.min(Math.max(y, 0), scope.bitmapSize.height - height);
  ctx.fillStyle = withAlpha(theme.surface, 0.94);
  ctx.beginPath();
  ctx.roundRect(bx, by, width, height, 4 * hpr);
  ctx.fill();
  ctx.strokeStyle = border;
  ctx.lineWidth = hpr;
  ctx.stroke();
  ctx.fillStyle = theme.text;
  ctx.textBaseline = "top";
  lines.forEach((l, i) => ctx.fillText(l, bx + pad, by + pad / 2 + i * lineH));
  ctx.restore();
}
